import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { MatMenuTrigger } from '@angular/material/menu';
import {
  HeaderFooterService,
  NavigationBreadcrumbExpansion,
  NavigationBreadcrumbLevel,
  NavigationBreadcrumbSelection,
  SnackbarService,
} from '@shared/ui';
import { isMacPlatform } from '@shared/utils';
import { Analyst, unlabeled } from '@analysis/domain';
import {
  AnalystStore,
  AssetStore,
  NavigationStore,
  RoiStore,
  AnalysisStateStore,
  RoiChangeStore,
  MosaicGalleryPanels,
  FilterConfigStore,
  RoiStatsStore,
  MosaicPaginationStore,
  ViewContextStore,
} from '../../stores';
import {
  BreadcrumbNavigationEffects,
  MosaicRoiFeatureEffects,
  FilterConfigEffects,
  RoiRestoreEffects,
  RoiRevertEffects,
  RoiAnalysisReviewEffects,
  RoiSyncEffects,
  RoiStatsEffects,
  RoiLabelingEffects,
  SampleViewLoadingEffects,
} from '../../effects';
import { MosaicDashboardFacade } from './mosaic-dashboard.facade';
import {
  AI_ANALYSIS_READY,
  AI_ANALYSIS_SAVE_BLOCKED_I18N,
  SampleAiAnalysisProgressStore,
} from '../microscope-image/state';
import { LabelStore, MethodTypeStore, PipelineStore, ProtocolStore, StepStore } from '@telespot/protocols/ui';
import { CanComponentDeactivate } from '@telespot/shared/util';
import { Observable } from 'rxjs';
import { AiDisclaimerService } from '../../services/ai-disclaimer/ai-disclaimer.service';

export type ProtocolCellMenuPayload =
  | { kind: 'label'; labelName: string }
  | { kind: 'special'; specialKind: 'unlabeled' | 'changed' | 'selected' };

@Component({
  selector: 'ts-mosaic-dashboard',
  templateUrl: './mosaic-dashboard.component.html',
  styleUrls: ['./mosaic-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    MosaicDashboardFacade,
    NavigationStore,
    BreadcrumbNavigationEffects,
    ViewContextStore,
    SampleViewLoadingEffects,
    SampleAiAnalysisProgressStore,
    AnalystStore,
    AssetStore,
    RoiStore,
    RoiStatsStore,
    MosaicPaginationStore,
    RoiChangeStore,
    MosaicGalleryPanels,
    FilterConfigStore,
    AnalysisStateStore,
    ProtocolStore,
    PipelineStore,
    StepStore,
    LabelStore,
    MethodTypeStore,
    MosaicRoiFeatureEffects,
    FilterConfigEffects,
    RoiStatsEffects,
    RoiRestoreEffects,
    RoiLabelingEffects,
    RoiRevertEffects,
    RoiAnalysisReviewEffects,
    RoiSyncEffects,
  ],
})
export class MosaicDashboardComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly destroy$ = new Subject<void>();

  public readonly tootipDelay = 500;
  public metaKeyPressed = false;
  public readonly isMacPlatform = isMacPlatform();
  public contextMenuPosition = { x: '0px', y: '0px' };

  @ViewChild('protocolCellContextMenuTrigger') protocolCellContextMenuTrigger!: MatMenuTrigger;

  // TODO: add to drop list service
  public panelDropLists: string[] = [];
  public tabConnectedLists: string[] = [];

  public readonly sampleId$ = this.facade.sampleId$;
  public readonly caseId$ = this.facade.caseId$;
  public readonly unsynced$ = this.facade.unsynced$;
  public readonly loading$ = this.facade.loading$;
  public readonly totalActiveDetections$ = this.facade.totalActiveDetections$;
  public readonly totalDetections$ = this.facade.totalDetections$;
  public readonly labelCounts$ = this.facade.labelCounts$;
  public readonly activeLabelCounts$ = this.facade.activeLabelCounts$;
  public readonly sampleCategoryCounts$ = this.facade.sampleCategoryCounts$;
  public readonly syncing$ = this.facade.syncing$;
  public readonly busy$ = this.facade.busy$;
  public readonly labels$ = this.facade.labels$;
  public readonly pipelines$ = this.facade.pipelines$;
  public readonly categories$ = this.facade.categories$;
  public readonly selectedAnalyst$ = this.facade.selectedAnalyst$;
  public readonly focusedRois$ = this.facade.focusedPanelSelectedItems$;
  public readonly detailCollapsed$ = this.facade.detailCollapsed$;
  public readonly protocolCollapsed$ = this.facade.protocolCollapsed$;
  public readonly scale$ = this.facade.scale$;
  public readonly showOverlay$ = this.facade.showOverlay$;
  public readonly showRoiDetails$ = this.facade.showRoiDetails$;
  public readonly roiBoxVisible$ = this.facade.roiBoxVisible$;
  public readonly selectedAnalystIsCurrentUser$ = this.facade.selectedAnalystIsCurrentUser$;
  public readonly editingEnabled$ = this.facade.editingEnabled$;
  public readonly editingActive$ = this.facade.editingActive$;
  public readonly aiAnalysisViewOnly$ = this.facade.aiAnalysisViewOnly$;

  public readonly aiBannerVisible$ = this.aiAnalysisViewOnly$;
  public readonly aiPollingExhausted$ = this.sampleAiProgressStore.pollingExhausted$;
  /** The lock the stale-analysis banner explains: editing is unavailable until a reload. */
  public readonly refreshRequired$ = this.facade.refreshRequired$;

  /**
   * The stale-analysis banner, or undefined when the view is current.
   *
   * Three faces, and only the last two offer the refresh: still processing (a bar, and
   * refreshing now would gain nothing), finished (refresh to pick the new rows up), and
   * given up (the poll stopped advancing, so refreshing is the only way to find out).
   * Unknown progress reads as "still processing" so a re-measure cannot flash a button.
   */
  public readonly staleAnalysisBanner$ = combineLatest([
    this.facade.refreshRequired$,
    this.facade.projectionProgress$,
    this.facade.projectionPollingExhausted$,
  ]).pipe(
    map(([required, progress, exhausted]) => {
      if (!required) return undefined;

      const processing = !exhausted && !progress?.caughtUp;

      return {
        messageKey: exhausted
          ? 'analysis.stale_analysis'
          : processing
          ? 'analysis.stale_analysis_progress'
          : 'analysis.stale_analysis_ready',
        showRefresh: !processing,
        progress,
      };
    })
  );
  public readonly panelIds$ = this.facade.panelIds$;
  public readonly maxPanelsReached$ = this.facade.maxPanelsReached$;
  public readonly offlineMode$ = this.facade.offlineMode$;
  public readonly changes$ = this.facade.changes$;
  public readonly changeCountPerLabel$ = this.facade.changeCountPerLabel$;
  public readonly analysts$ = this.facade.analysts$;
  public readonly meOption$ = this.facade.currentUserAsAnalystOption$;
  public readonly tileSource$ = this.facade.tileSource$;
  public readonly selectedAssetId$ = this.facade.selectedAssetId$;
  public readonly categoryChangeCounts$ = this.facade.categoryChangeCounts$;
  public readonly statsMode$ = this.facade.statsMode$;
  public readonly protocolLoading$ = this.facade.protocolLoading$;
  public readonly statsLoading$ = this.facade.statsLoading$;
  public readonly excludedFromCounter$ = this.facade.excludedFromCounter$;
  public readonly focusedPanelId$ = this.facade.focusedPanelId$;
  public readonly breadcrumbLevels$ = this.facade.breadcrumbLevels$;
  public readonly trackByPanelId = this.facade.trackByPanelId;

  disclaimerAccepted = true;

  constructor(
    private readonly facade: MosaicDashboardFacade,
    private readonly headerFooterService: HeaderFooterService,
    private readonly snackbarService: SnackbarService,
    private readonly sampleAiProgressStore: SampleAiAnalysisProgressStore,
    private readonly disclaimerService: AiDisclaimerService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.headerFooterService.toggleHeaderAndFooter(false);
  }

  ngOnInit(): void {
    this.facade.saveBlockedByAiAnalysis$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() =>
        this.snackbarService.showWithBoldLead(
          AI_ANALYSIS_SAVE_BLOCKED_I18N.title,
          AI_ANALYSIS_SAVE_BLOCKED_I18N.body,
          { duration: 12_000 }
        )
      );

    this.sampleAiProgressStore.aiAnalysisCompleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.snackbarService.show(AI_ANALYSIS_READY, { duration: 6000 }));

    this.selectedAnalyst$
      .pipe(
        distinctUntilChanged((previous, current) => previous?.id === current?.id),
        takeUntil(this.destroy$)
      )
      .subscribe((analyst) => this.syncDisclaimerState(analyst));
  }

  public isAnalystSelectDisabled(analyst: Analyst | null | undefined): boolean {
    return analyst?.type === 'algorithm' && !this.disclaimerAccepted;
  }

  private syncDisclaimerState(analyst: Analyst | null | undefined): void {
    this.disclaimerAccepted = this.disclaimerService.getDisclaimerAcceptedState(analyst);
    this.cdr.markForCheck();

    if (this.disclaimerAccepted) {
      return;
    }

    this.disclaimerService.ensureDisclaimerAccepted(analyst).subscribe((accepted) => {
      this.disclaimerAccepted = accepted;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.headerFooterService.toggleHeaderAndFooter(true);
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.facade.hasUnsyncedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  public canDeactivate(): Observable<boolean> {
    return this.facade.canDeactivate();
  }

  // TODO: add to layout store
  public updateTabDropLists(ids: string[], index: number): void {
    const nonIndexLists = this.tabConnectedLists.filter((list) => !list.startsWith(`${index}-`));
    const uniqueIds = new Set([...nonIndexLists, ...ids, ...this.panelDropLists, unlabeled]);
    this.tabConnectedLists = Array.from(uniqueIds);
  }

  public sync(): void {
    this.facade.sync();
  }

  public exit(): void {
    this.facade.exit();
  }

  public acceptCompleteAnalysis(): void {
    this.facade.acceptCompleteAnalysis();
  }

  public openDiscardChangesDialog(): void {
    this.facade.openDiscardChangesDialog();
  }

  public onLabelSelected(labelName: string): void {
    this.facade.onLabelSelected(labelName, this.metaKeyPressed);
  }

  public onProtocolCellContextMenu(event: MouseEvent, payload: ProtocolCellMenuPayload): void {
    if (event.metaKey || event.ctrlKey) {
      this.openInNewPanel(payload);
      return;
    }
    this.contextMenuPosition = { x: `${event.clientX}px`, y: `${event.clientY}px` };
    this.protocolCellContextMenuTrigger.menuData = { payload };
    this.protocolCellContextMenuTrigger.openMenu();
  }

  public openInFocusedPanel(payload: ProtocolCellMenuPayload): void {
    if (payload.kind === 'label') {
      this.facade.openLabelInFocusedPanel(payload.labelName);
    } else {
      this.facade.openSpecialInFocusedPanel(payload.specialKind);
    }
  }

  public openInNewPanel(payload: ProtocolCellMenuPayload): void {
    if (payload.kind === 'label') {
      this.facade.openLabelInNewPanel(payload.labelName);
    } else {
      this.facade.openSpecialInNewPanel(payload.specialKind);
    }
  }

  public addLabelToFocusedItems(label: string): void {
    this.facade.addLabelToFocusedItems(label);
  }

  public loadAnalysts(sampleId: string, caseId: string): void {
    this.facade.loadAnalysts(sampleId);
    this.facade.loadAnalysisStates(sampleId, caseId);
  }

  public selectAnalyst(analyst: Analyst): void {
    this.facade.selectAnalyst(analyst);
  }

  /** While an algorithm analyst is selected and its disclaimer not yet accepted,
   *  the analyst level cannot be switched — same rule the mat-select enforced. */
  public applyAnalystDisabledState(
    levels: NavigationBreadcrumbLevel[] | null,
    selectedAnalyst: Analyst | null | undefined
  ): NavigationBreadcrumbLevel[] {
    if (!levels) return [];
    if (!this.isAnalystSelectDisabled(selectedAnalyst)) return levels;
    return levels.map((level) => (level.key === 'analyst' ? { ...level, disabled: true } : level));
  }

  /**
   * The analyst is this breadcrumb's deepest level, so it is the only one that commits —
   * every level above it expands instead.
   *
   * A pick that came through a cascade carries a sample in its path: that is a location
   * change, committed as sample + analyst in one navigation so the mosaic loads once. A
   * pick straight from the analyst chip has no sample in its path and only switches the
   * analyst on the open sample.
   */
  public onBreadcrumbSelect(
    selection: NavigationBreadcrumbSelection,
    currentCaseId: string,
    meOption: Analyst | null,
    analysts: Analyst[] | null
  ): void {
    if (selection.levelKey !== 'analyst') return;

    const sampleId = this.stepFromPath(selection, 'sample');
    if (sampleId) {
      const caseId = this.stepFromPath(selection, 'case') ?? currentCaseId;
      if (caseId) this.facade.openSampleAsAnalyst(caseId, sampleId, selection.optionId);
      return;
    }

    const analyst = [meOption, ...(analysts ?? [])].find((candidate) => candidate?.id === selection.optionId);
    if (analyst) this.selectAnalyst(analyst);
  }

  private stepFromPath(selection: NavigationBreadcrumbSelection, levelKey: string): string | undefined {
    return selection.path?.find((step) => step.levelKey === levelKey)?.optionId;
  }

  public onBreadcrumbLevelOpened(levelKey: string, sampleId: string, caseId: string): void {
    if (levelKey !== 'analyst') return;
    this.loadAnalysts(sampleId, caseId);
  }

  /** A cascading option was opened: load the level below it. */
  public onBreadcrumbOptionExpanded(expansion: NavigationBreadcrumbExpansion): void {
    if (expansion.levelKey === 'workspace') this.facade.loadCasesForWorkspace(expansion.optionId);
    if (expansion.levelKey === 'case') this.facade.loadSamplesForCase(expansion.optionId);
    if (expansion.levelKey === 'sample') this.facade.loadAnalystsForSample(expansion.optionId);
  }

  public navigateToMicro(caseId: string, sampleId: string): void {
    this.facade.navigateToMicro(caseId, sampleId);
  }

  public clearFocusedRoi(): void {
    this.facade.clearFocusedRoi();
  }

  public setFocusedRoi(id: string): void {
    this.facade.setFocusedRoi(id);
  }

  public toggleCounterExcludedLabel(id: string): void {
    this.facade.toggleCounterExcludedLabel(id);
  }

  public toggleProtocol(): void {
    this.facade.toggleProtocol();
  }

  public toggleDetail(): void {
    this.facade.toggleDetail();
  }

  public openDetail(): void {
    this.facade.openDetail();
  }

  public toggleOverlay(): void {
    this.facade.toggleOverlay();
  }

  public toggleRoiDetails(): void {
    this.facade.toggleRoiDetails();
  }

  public setTileSize(size: number): void {
    this.facade.setTileSize(size);
  }

  public setRoiBoxVisible(visible: boolean): void {
    this.facade.setRoiBoxVisible(visible);
  }

  public toggleEditing(enabled: boolean): void {
    this.facade.toggleEditing(enabled);
  }

  public addPanel(): void {
    this.facade.addPanel();
  }

  public focusNextPanel(): void {
    this.facade.focusNextPanel();
  }

  public focusPreviousPanel(): void {
    this.facade.focusPreviousPanel();
  }

  public focusPanelByOrder(order: number): void {
    this.facade.focusPanelByOrder(order);
  }

  public removePanel(id: number): void {
    this.facade.removePanel(id);
  }

  public removeAllLabelsOnFocusedItems(): void {
    this.facade.removeAllLabelsOnFocusedItems();
  }

  public onSpecialSelected(kind: 'unlabeled' | 'changed' | 'selected'): void {
    this.facade.onSpecialSelected(kind, this.metaKeyPressed);
  }

  public filterFocusedPanelByAsset(assetId: string): void {
    this.facade.filterFocusedPanelByAsset(assetId);
  }

  public filterFocusedPanelBySelected(): void {
    this.facade.filterFocusedPanelBySelected();
  }

  public toggleCounterExclusionMode(mode: string): void {
    this.facade.toggleCounterExclusionMode(mode);
  }
}
